import { Typography } from "@mui/material";
import { useAchievements } from "api/achievements";
import { useWallets } from "api/wallets";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { ReactComponent as InfoCircleIcon } from "components/Icons/info-circle.svg";
import { ExportProgressModal } from "components/Layout/Achievement/AchievementList/ImportAchievements/ExportProgressModal";
import { prepareExportCampaignsJson } from "components/Layout/Campaign/Common/campaignHelpers";
import { prepareExportCustomEventSchemasJson } from "components/Layout/CustomEvent/utils";
import { prepareExportRewardsJson } from "components/Layout/Reward/RewardList/helpers";
import { TargetTenantModal } from "components/TargetTenantModal/TargetTenantModal";
import { OLModal } from "components/Tooltips/OLModal";
import { useMessage } from "hooks/intl";
import { useEffect, useState } from "react";
import { useBlocker } from "react-router-dom";
import {
  selectCurrentStoreData,
  setCurrentStoreData,
} from "reduxState/currentStoreData/currentStoreDataSlice";
import {
  selectIsDuplicateIntroMessageSeen,
  setImported,
  setIsDuplicateIntroMessageSeen,
} from "reduxState/globalManagement/globalManagement";
import { links } from "utilities/links";
import { useStyles } from "./styles";
import { AchievementsTable } from "./Tables/Achievements";
import { AutomationsTable } from "./Tables/Automations";
import { CampaignsTable } from "./Tables/Campaigns";
import { CustomEventSchemaTable } from "./Tables/CustomEventSchema";
import { ReferralsTable } from "./Tables/Referrals";
import { RewardsTable } from "./Tables/Rewards";
import { WalletsTable } from "./Tables/Wallets";
import { Tab, Tabs } from "./Tabs/Tabs";

export type Resource = {
  id: string;
  data: any;
};

export type SelectedResources = {
  [key in Tab]?: Resource[];
};

export const ConfigDuplication = () => {
  const [selectedResources, setSelectedResources] = useState<SelectedResources>(
    {},
  );
  const [isProcessingExport, setIsProcessingExport] = useState(false);
  const [isDifferentTenantModalOpen, setIsDifferentTenantModalOpen] =
    useState(false);
  const [originStore] = useState(useAppSelector(selectCurrentStoreData));
  const [activeTab, setActiveTab] = useState<Tab>();
  const { message } = useMessage();
  const dispatch = useAppDispatch();
  const isDuplicateIntroMessageSeen = useAppSelector(
    selectIsDuplicateIntroMessageSeen,
  );
  const [isIntroModal, setIsIntroModal] = useState(false);
  const [exportData, setExportData] = useState({});
  const classes = useStyles();
  const { getWalletTypeBatchData } = useWallets();
  const { getAchievementBatchData } = useAchievements();
  const markIntroModalAsSeen = () => {
    dispatch(setIsDuplicateIntroMessageSeen(true));
    setIsIntroModal(false);
  };
  const maxSelectionPerTable = 50;

  useEffect(() => {
    if (!isDuplicateIntroMessageSeen) {
      setIsIntroModal(true);
    }
  }, [isDuplicateIntroMessageSeen, dispatch]);

  useBlocker(({ nextLocation }) => {
    if (nextLocation.pathname !== links.configDuplicationImport) {
      // Bring back user tenant when duplication is canceled
      dispatch(setCurrentStoreData(originStore));
    }
    return false;
  });

  const selectRow = (tab: Tab, row: Resource) => {
    return setSelectedResources({
      ...selectedResources,
      [tab]: [...(selectedResources[tab] || []), row],
    });
  };

  const unselectRow = (tab: Tab, id: string) => {
    const rows = [...(selectedResources[tab] || [])];
    const indexToRemove = rows.findIndex(
      (selectedRow) => selectedRow.id === id,
    );
    rows.splice(indexToRemove, 1);

    return setSelectedResources({
      ...selectedResources,
      [tab]: rows,
    });
  };

  const selectAll = (tab: Tab, selectedRows: Resource[]) => {
    const rows = [...(selectedResources[tab] || [])];

    selectedRows.forEach((selectedRow) => {
      const isAlreadySelected = rows.find((row) => row.id === selectedRow.id);
      const isLimitReached = rows.length >= maxSelectionPerTable;

      if (!isAlreadySelected && !isLimitReached) {
        rows.push(selectedRow);
      }
    });

    return setSelectedResources({
      ...selectedResources,
      [tab]: rows,
    });
  };

  const unselectAll = (tab: Tab, unselectedRows: string[]) => {
    const rows = [...(selectedResources[tab] || [])].filter(
      ({ id }) => !unselectedRows.includes(id),
    );

    return setSelectedResources({
      ...selectedResources,
      [tab]: rows,
    });
  };

  const clearSelection = () => setSelectedResources({});

  const selectedResourcesCount = Object.values(selectedResources).reduce(
    (total, arr) => total + arr.length,
    0,
  );

  const fetchResourcesData = async () => {
    setIsProcessingExport(true);

    const wallets = await getWalletTypeBatchData(
      selectedResources[Tab.WALLET]?.map(({ id }) => ({
        walletTypeId: id,
      })) || [],
    );
    const customEvents = prepareExportCustomEventSchemasJson(
      selectedResources[Tab.CUSTOM_EVENT]?.map(({ data }) => data) || [],
    );
    const rewards = prepareExportRewardsJson(
      selectedResources[Tab.REWARDS]?.map(({ data }) => data) || [],
    );
    const achievements = await getAchievementBatchData(
      selectedResources[Tab.ACHIEVEMENTS]?.map(
        ({ data: { achievementId, name } }) => ({
          achievementId,
          name,
        }),
      ) || [],
    );
    const campaigns = prepareExportCampaignsJson(
      selectedResources[Tab.CAMPAIGNS]?.map(({ data }) => data) || [],
    );
    const referrals = prepareExportCampaignsJson(
      selectedResources[Tab.REFERRALS]?.map(({ data }) => data) || [],
    );
    const automations = prepareExportCampaignsJson(
      selectedResources[Tab.AUTOMATIONS]?.map(({ data }) => data) || [],
    );
    const isWalletsData = wallets && wallets.length > 0;
    const isCustomEventsData = customEvents && customEvents.length > 0;
    const isRewardsData = rewards && rewards.length > 0;
    const isAchievementsData = achievements && achievements.length > 0;
    const isCampaignsData = campaigns && campaigns.length > 0;
    const isReferralsData = referrals && referrals.length > 0;
    const isAutomationsData = automations && automations.length > 0;

    const exportData = {
      ...(isWalletsData
        ? { wallets: wallets.map((wallet) => ({ ...wallet, active: false })) }
        : {}),
      ...(isCustomEventsData ? { customEvents } : {}),
      ...(isRewardsData
        ? { rewards: rewards.map((reward) => ({ ...reward, active: false })) }
        : {}),
      ...(isAchievementsData ? { achievements } : {}),
      ...(isCampaignsData ? { campaigns } : {}),
      ...(isReferralsData ? { referrals } : {}),
      ...(isAutomationsData ? { automations } : {}),
    };

    setExportData(exportData);
    setIsDifferentTenantModalOpen(true);
  };

  return (
    <>
      <TopHeader title={message({ id: "globalManagement" })} />
      <Tabs
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        selectedCount={selectedResourcesCount}
        fetchResourcesData={fetchResourcesData}
      />
      {activeTab === Tab.WALLET && (
        <WalletsTable
          rowsSelected={selectedResources[Tab.WALLET] || []}
          selectRow={(row: Resource) => selectRow(Tab.WALLET, row)}
          unselectRow={(id: string) => unselectRow(Tab.WALLET, id)}
          selectAll={(rows: Resource[]) => selectAll(Tab.WALLET, rows)}
          unselectAll={(ids: string[]) => unselectAll(Tab.WALLET, ids)}
          clearSelection={clearSelection}
          selectionDisabled={
            (selectedResources[Tab.WALLET] || []).length >= maxSelectionPerTable
          }
        />
      )}
      {activeTab === Tab.CUSTOM_EVENT && (
        <CustomEventSchemaTable
          rowsSelected={selectedResources[Tab.CUSTOM_EVENT] || []}
          selectRow={(row: Resource) => selectRow(Tab.CUSTOM_EVENT, row)}
          unselectRow={(id: string) => unselectRow(Tab.CUSTOM_EVENT, id)}
          selectAll={(rows: Resource[]) => selectAll(Tab.CUSTOM_EVENT, rows)}
          unselectAll={(ids: string[]) => unselectAll(Tab.CUSTOM_EVENT, ids)}
          clearSelection={clearSelection}
          selectionDisabled={
            (selectedResources[Tab.CUSTOM_EVENT] || []).length >=
            maxSelectionPerTable
          }
        />
      )}
      {activeTab === Tab.REWARDS && (
        <RewardsTable
          rowsSelected={selectedResources[Tab.REWARDS] || []}
          selectRow={(row: Resource) => selectRow(Tab.REWARDS, row)}
          unselectRow={(id: string) => unselectRow(Tab.REWARDS, id)}
          selectAll={(rows: Resource[]) => selectAll(Tab.REWARDS, rows)}
          unselectAll={(ids: string[]) => unselectAll(Tab.REWARDS, ids)}
          clearSelection={clearSelection}
          selectionDisabled={
            (selectedResources[Tab.REWARDS] || []).length >=
            maxSelectionPerTable
          }
        />
      )}
      {activeTab === Tab.ACHIEVEMENTS && (
        <AchievementsTable
          rowsSelected={selectedResources[Tab.ACHIEVEMENTS] || []}
          selectRow={(row: Resource) => selectRow(Tab.ACHIEVEMENTS, row)}
          unselectRow={(id: string) => unselectRow(Tab.ACHIEVEMENTS, id)}
          selectAll={(rows: Resource[]) => selectAll(Tab.ACHIEVEMENTS, rows)}
          unselectAll={(ids: string[]) => unselectAll(Tab.ACHIEVEMENTS, ids)}
          clearSelection={clearSelection}
          selectionDisabled={
            (selectedResources[Tab.ACHIEVEMENTS] || []).length >=
            maxSelectionPerTable
          }
        />
      )}
      {activeTab === Tab.CAMPAIGNS && (
        <CampaignsTable
          rowsSelected={selectedResources[Tab.CAMPAIGNS] || []}
          selectRow={(row: Resource) => selectRow(Tab.CAMPAIGNS, row)}
          unselectRow={(id: string) => unselectRow(Tab.CAMPAIGNS, id)}
          selectAll={(rows: Resource[]) => selectAll(Tab.CAMPAIGNS, rows)}
          unselectAll={(ids: string[]) => unselectAll(Tab.CAMPAIGNS, ids)}
          clearSelection={clearSelection}
          selectionDisabled={
            (selectedResources[Tab.CAMPAIGNS] || []).length >=
            maxSelectionPerTable
          }
        />
      )}
      {activeTab === Tab.REFERRALS && (
        <ReferralsTable
          rowsSelected={selectedResources[Tab.REFERRALS] || []}
          selectRow={(row: Resource) => selectRow(Tab.REFERRALS, row)}
          unselectRow={(id: string) => unselectRow(Tab.REFERRALS, id)}
          selectAll={(rows: Resource[]) => selectAll(Tab.REFERRALS, rows)}
          unselectAll={(ids: string[]) => unselectAll(Tab.REFERRALS, ids)}
          clearSelection={clearSelection}
          selectionDisabled={
            (selectedResources[Tab.REFERRALS] || []).length >=
            maxSelectionPerTable
          }
        />
      )}
      {activeTab === Tab.AUTOMATIONS && (
        <AutomationsTable
          rowsSelected={selectedResources[Tab.AUTOMATIONS] || []}
          selectRow={(row: Resource) => selectRow(Tab.AUTOMATIONS, row)}
          unselectRow={(id: string) => unselectRow(Tab.AUTOMATIONS, id)}
          selectAll={(rows: Resource[]) => selectAll(Tab.AUTOMATIONS, rows)}
          unselectAll={(ids: string[]) => unselectAll(Tab.AUTOMATIONS, ids)}
          clearSelection={clearSelection}
          selectionDisabled={
            (selectedResources[Tab.AUTOMATIONS] || []).length >=
            maxSelectionPerTable
          }
        />
      )}
      <OLModal
        header={message({ id: "introducingConfigDuplication" })}
        onClose={() => setIsIntroModal(false)}
        open={isIntroModal}
        submitText={message({ id: "confirm" })}
        submitAction={markIntroModalAsSeen}
        modalWidth={536}
      >
        <>
          <Typography className={classes.introInfo}>
            {message({ id: "introConfigDuplicationDescription" })}
          </Typography>
          <div className={classes.infroTenantBox}>
            <InfoCircleIcon className={classes.icon} />
            <Typography fontSize={14}>
              {message({ id: "introConfigDuplicationTargetTenant" })}
            </Typography>
          </div>
        </>
      </OLModal>
      <ExportProgressModal
        isProcessingExport={isProcessingExport}
        modalWidth={450}
        setIsProcessingExport={setIsProcessingExport}
        infoTitle={message({ id: "duplicateConfigurationInProgress" })}
      />
      <TargetTenantModal
        isOpen={isDifferentTenantModalOpen}
        headerText={message({ id: "duplicateConfigurationToDiffTenant" })}
        onClose={() => {
          setIsProcessingExport(false);
          setIsDifferentTenantModalOpen(false);
        }}
        setJsonAction={setImported}
        data={exportData}
        link={links.configDuplicationImport}
        infoText={message({ id: "configDuplicationInformation" })}
        buttonText={message({ id: "duplicateAndViewTargetTenant" })}
      />
    </>
  );
};
